import type { StyleLibrary } from "schemas/generated/project";
import type { FontPreset } from "./utils/presets";

import * as React from "react";

import { useInsertGoogleFontsUrl } from "@editor/hooks/designLibrary/useInsertGoogleFontsUrl";

import IconButton from "@replo/design-system/components/button/IconButton";
import twMerge from "@replo/design-system/utils/twMerge";
import { ArrowLeft, Check } from "lucide-react";

import { fontPresets } from "./utils/presets";

const allFonts = fontPresets.flatMap((preset) =>
  [preset.preset1, preset.preset2].flatMap((subPreset) =>
    Object.values(subPreset).map((font) => ({
      name: font.name,
      weight: font.weight,
    })),
  ),
);

interface PresetOptionProps {
  presetData: FontPreset["preset1"] | FontPreset["preset2"];
  isSelected: boolean;
  onSelect: () => void;
}

const PresetOption: React.FC<PresetOptionProps> = ({
  presetData,
  isSelected,
  onSelect,
}) => {
  return (
    <div
      className={twMerge(
        "flex-1 flex flex-col p-3 justify-center items-center h-18 rounded-lg bg-sheet border-0.5 cursor-pointer relative gap-1",
        isSelected ? "border-black border-2" : "border-black/10",
      )}
      onClick={onSelect}
    >
      {isSelected && (
        <div className="absolute -top-3 -right-3 z-10 w-6 h-6 bg-black rounded-full flex items-center justify-center">
          <Check size={16} className="text-white" />
        </div>
      )}
      <div
        className="text-center leading-none"
        style={{
          fontFamily: presetData.h1.name,
          fontSize: "24px",
          fontWeight: presetData.h1.weight,
        }}
      >
        Heading
      </div>
      <div
        className="text-center leading-none"
        style={{
          fontFamily: presetData.body.name,
          fontSize: "12px",
          fontWeight: presetData.body.weight,
        }}
      >
        This is a paragraph.
      </div>
    </div>
  );
};

interface PresetBlockProps {
  preset: FontPreset;
  presetIndex: number;
  selectedPreset: { presetIndex: number; subPreset: 1 | 2 } | null;
  onPresetSelect: (presetIndex: number, subPreset: 1 | 2) => void;
}

const PresetBlock: React.FC<PresetBlockProps> = ({
  preset,
  presetIndex,
  selectedPreset,
  onPresetSelect,
}) => {
  const isPreset1Selected =
    selectedPreset?.presetIndex === presetIndex &&
    selectedPreset?.subPreset === 1;
  const isPreset2Selected =
    selectedPreset?.presetIndex === presetIndex &&
    selectedPreset?.subPreset === 2;

  return (
    <div className="w-full flex flex-col flex-shrink-0">
      <div className="text-base text-muted">{preset.title}</div>
      <div className="flex gap-2 mt-2">
        <PresetOption
          presetData={preset.preset1}
          isSelected={isPreset1Selected}
          onSelect={() => onPresetSelect(presetIndex, 1)}
        />
        <PresetOption
          presetData={preset.preset2}
          isSelected={isPreset2Selected}
          onSelect={() => onPresetSelect(presetIndex, 2)}
        />
      </div>
    </div>
  );
};

interface DesignLibraryFontPresetsProps {
  styleLibrary: StyleLibrary | null;
  onBack?: () => void;
  onStyleLibraryChange: (styleLibrary: StyleLibrary) => void;
}

export const DesignLibraryFontPresets: React.FC<
  DesignLibraryFontPresetsProps
> = ({ styleLibrary, onBack, onStyleLibraryChange }) => {
  useInsertGoogleFontsUrl(allFonts);

  const [selectedPreset, setSelectedPreset] = React.useState<{
    presetIndex: number;
    subPreset: 1 | 2;
  } | null>(null);

  const handlePresetSelect = (presetIndex: number, subPreset: 1 | 2) => {
    if (!styleLibrary) {
      return;
    }

    const preset = fontPresets[presetIndex];
    if (!preset) {
      return;
    }

    const selectedSubPreset = subPreset === 1 ? preset.preset1 : preset.preset2;

    setSelectedPreset({ presetIndex, subPreset });

    const updatedTypography = {
      h1: selectedSubPreset.h1,
      h2: selectedSubPreset.h2,
      h3: selectedSubPreset.h3,
      h4: selectedSubPreset.h4,
      body: selectedSubPreset.body,
    };

    onStyleLibraryChange({
      ...styleLibrary,
      typography: updatedTypography,
    });
  };

  return (
    <div className="flex flex-col items-start flex-[1_0_0] self-stretch min-h-0 rounded-xl border border-border bg-white shadow-button-selectable">
      <div className="flex justify-between items-center self-stretch p-6">
        <div className="flex items-center gap-3">
          <IconButton
            variant="secondary"
            size="sm"
            layoutClassName="h-6 w-6"
            icon={<ArrowLeft size={16} className="text-default" />}
            onClick={() => {
              onBack?.();
            }}
          />
          <div className="text-lg font-semibold">Typography Presets</div>
        </div>
      </div>
      <div className="flex flex-1 flex-col self-stretch overflow-y-auto no-scrollbar p-6 pt-0 gap-6">
        {fontPresets.map((preset, index) => (
          <PresetBlock
            key={index}
            preset={preset}
            presetIndex={index}
            selectedPreset={selectedPreset}
            onPresetSelect={handlePresetSelect}
          />
        ))}
      </div>
    </div>
  );
};
